// LogoutConfirmationModal - Confirmation modal for user logout
// Shows when user attempts to logout, following UnsavedChangesModal design pattern

import React from 'react';

export interface LogoutConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when user clicks "Cancel" to stay logged in */
  onCancel: () => void;
  /** Called when user clicks "Logout" to confirm logout */
  onConfirm: () => void;
  /** Title of the modal */
  title?: string;
  /** Description text */
  message?: string;
  /** Additional CSS classes */
  className?: string;
}

const LogoutConfirmationModal: React.FC<LogoutConfirmationModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  title = "Confirm Logout",
  message = "Are you sure you want to logout? You will be redirected to the login page.",
  className = "",
}) => {
  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[140] tw-flex tw-items-center tw-justify-center tw-p-4">
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-backdrop-blur-sm tw-transition-opacity"
        onClick={onCancel}
      />

      {/* Modal */}
      <div
        className={`tw-relative tw-w-full tw-max-w-md tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-shadow-xl tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-overflow-hidden ${className}`}
      >
        {/* Header */}
        <div className="tw-p-6 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
          <div className="tw-flex tw-items-start tw-gap-4">
            {/* Logout Icon */}
            <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-bg-blue-100 dark:tw-bg-blue-900/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <span className="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400 tw-text-[24px]">
                logout
              </span>
            </div>
            
            <div className="tw-flex-1">
              <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-2">
                {title}
              </h3>
              <p className="tw-text-sm tw-text-gray-600 dark:tw-text-gray-300 tw-leading-relaxed">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="tw-p-6 tw-bg-gray-50 dark:tw-bg-[#151f28]">
          <div className="tw-flex tw-flex-col-reverse sm:tw-flex-row tw-gap-3 sm:tw-justify-end">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className="tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg hover:tw-bg-gray-50 dark:hover:tw-bg-[#233648] tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
            >
              Cancel
            </button>

            {/* Logout Button */}
            <button
              onClick={onConfirm}
              className="tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 tw-rounded-lg hover:tw-bg-red-700 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 focus:tw-ring-offset-2"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmationModal;